import { Button } from "@mantine/core";
import React, { useState, useEffect } from "react";
import apiService from "../../services/api";
import type { ClientPortalProps } from "../../types";
import { parseListString, parsePrice } from "../../utils/formatting";
import PortalLayout, { type PortalTab } from "../shared/PortalLayout";
import AddPetModal from "./modals/AddPetModal";
import CancelRequestModal from "./modals/CancelRequestModal";
import CouponModal from "./modals/CouponModal";
import TrainingRequestModal from "./modals/TrainingRequestModal";
import ClientProfileTab from "./tabs/ClientProfileTab";
import ClientRequestsTab from "./tabs/ClientRequestsTab";
import ClientSessionsTab from "./tabs/ClientSessionsTab";
import FindTrainerTab from "./tabs/FindTrainerTab";
import PetsTab from "./tabs/PetsTab";
import type { LocalPet, TransformedTrainer } from "../../types/portal";

interface SessionHistoryItem {
  id: string;
  session_request_id?: string;
  trainer_id?: string;
  trainer_name?: string;
  date?: string;
  end_date?: string;
  duration_minutes?: number;
  session_duration_minutes?: number;
  price?: number;
  status?: string;
  summary?: string;
  training_tasks?: unknown;
  user_rating?: number | null;
  [key: string]: unknown;
}

interface TrainingRequest {
  id: string;
  trainer_name?: string;
  status: string;
  first_10_min_rate: number;
  per_minute_rate: number;
  total_price: number | null;
  duration_minutes: number | null;
  created_at: string | null;
  accepted_at: string | null;
  payment_status: string;
  client_joined_at: string | null;
  trainer_joined_at: string | null;
  notes?: string;
  [key: string]: unknown;
}

interface ClientProfile {
  first_name: string;
  last_name: string;
  email: string;
  phone_number?: string;
  address?: string;
  profile_photo_url?: string;
  favorite_trainer?: { name: string } | null;
  [key: string]: unknown;
}

interface ProfileEditData {
  first_name: string;
  last_name: string;
  phone_number: string;
  address: string;
  password: string;
  confirmPassword: string;
}

interface NewPetData {
  pet_name: string;
  age: string;
  race: string;
  target_behaviors: string;
}

interface DeliverableData {
  summary?: string;
  checklist_items?: string[];
  [key: string]: unknown;
}

const ClientPortal: React.FC<ClientPortalProps> = ({ onBackToHome, onLogout, isLoggedIn }) => {
  const [activeTab, setActiveTab] = useState<string>("find-trainer");
  const [pets, setPets] = useState<LocalPet[]>([]);
  const [profile, setProfile] = useState<ClientProfile | null>(null);
  const [activeTrainers, setActiveTrainers] = useState<TransformedTrainer[]>([]);
  const [sessionHistory, setSessionHistory] = useState<SessionHistoryItem[]>([]);
  const [sessionDeliverables, setSessionDeliverables] = useState<Record<string, DeliverableData>>(
    {},
  ); // Map session_request_id to deliverables
  const [activeTrainingRequests, setActiveTrainingRequests] = useState<TrainingRequest[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmPopup, setShowConfirmPopup] = useState<boolean>(false);
  const [selectedTrainer, setSelectedTrainer] = useState<TransformedTrainer | null>(null);
  const [showCancelPopup, setShowCancelPopup] = useState<boolean>(false);
  const [selectedRequest, setSelectedRequest] = useState<TrainingRequest | null>(null);
  const [selectedPetId, setSelectedPetId] = useState<string>("");
  const [requestExplanation, setRequestExplanation] = useState<string>("");
  const [requestDuration, setRequestDuration] = useState<number>(30);
  const [isEditingProfile, setIsEditingProfile] = useState<boolean>(false);
  const [profileEditData, setProfileEditData] = useState<ProfileEditData>({
    first_name: "",
    last_name: "",
    phone_number: "",
    address: "",
    password: "",
    confirmPassword: "",
  });
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [refreshSuccess, setRefreshSuccess] = useState<boolean>(false);
  const [selectedSession, setSelectedSession] = useState<SessionHistoryItem | null>(null);
  const [showSessionModal, setShowSessionModal] = useState<boolean>(false);
  const [showAddPetModal, setShowAddPetModal] = useState<boolean>(false);
  const [newPetData, setNewPetData] = useState<NewPetData>({
    pet_name: "",
    age: "",
    race: "",
    target_behaviors: "",
  });
  const [showCouponModal, setShowCouponModal] = useState<boolean>(false);
  const [uploadingPhoto, setUploadingPhoto] = useState<boolean>(false);
  const [selectedRequestForPayment, setSelectedRequestForPayment] =
    useState<TrainingRequest | null>(null);
  const [couponCode, setCouponCode] = useState<string>("");

  const clientId = localStorage.getItem("clientId");

  useEffect(() => {
    if (clientId) {
      loadInitialData();
    }
  }, [clientId]);

  const loadInitialData = async (): Promise<void> => {
    try {
      setLoading(true);

      // Load trainers by default since activeTab starts as 'find-trainer'
      if (activeTab === "find-trainer") {
        await loadActiveTrainers();
      } else if (activeTab === "pets") {
        await loadPets();
      }
    } catch (err) {
      console.error("Error loading initial data:", err);
      setError("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const loadPets = async (): Promise<void> => {
    try {
      const response = (await apiService.getClientPets(clientId!)) as Record<string, unknown>;
      // Backend returns "Data" (capital D), not "data"
      const petsData = (response.Data as LocalPet[]) || [];
      setPets(petsData);
    } catch (err) {
      console.error("Error loading pets:", err);
      setError("Failed to load pets");
    }
  };

  const loadProfile = async (): Promise<void> => {
    try {
      const response = (await apiService.getClientProfile(clientId!)) as Record<string, unknown>;
      if (response.Code === 200) {
        setProfile(response.data as ClientProfile);
      } else {
        setProfile(null);
      }
    } catch (err) {
      console.error("Error loading profile:", err);
      setError("Failed to load profile");
    }
  };

  const handleClientPhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setError("Please select an image file");
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError("Image size must be less than 10MB");
      return;
    }

    if (!clientId) {
      setError("Client ID not found");
      return;
    }

    try {
      setUploadingPhoto(true);
      setError(null);

      const response = (await apiService.uploadClientProfilePhoto(clientId!, file)) as Record<
        string,
        unknown
      >;

      if (response.Code === 200) {
        // Reload profile to get updated photo URL
        await loadProfile();
        alert("Profile photo uploaded successfully!");
      } else {
        setError((response.Message as string) || "Failed to upload photo");
      }
    } catch (err) {
      console.error("Photo upload error:", err);
      setError((err as Error).message || "Failed to upload photo");
    } finally {
      setUploadingPhoto(false);
      // Reset file input
      e.target.value = "";
    }
  };

  const loadActiveTrainers = async (): Promise<void> => {
    try {
      setRefreshSuccess(false);
      setLoading(true);

      // Force a fresh API call by adding a timestamp parameter
      const response = (await apiService.getActiveTrainers()) as Record<string, unknown>;
      const trainersArray = response.trainers as Record<string, unknown>[] | undefined;

      if (response.Code === 200 && trainersArray && trainersArray.length > 0) {

        // Transform backend data to match frontend format
        const transformedTrainers = trainersArray.map((trainer: Record<string, unknown>) => {
          const first10MinRate = parsePrice(trainer.first_10_min_rate);
          const perMinuteRate = parsePrice(trainer.per_minute_rate);
          const expertise = parseListString(trainer.areas_of_expertise);
          const philosophy = parseListString(trainer.training_philosophy);
          const credentials = parseListString(trainer.credentials);

          return {
            id: trainer.id as string,
            name: `${trainer.first_name as string} ${trainer.last_name as string}`,
            title: `Professional Dog Trainer`,
            experience: `${trainer.experience_years || 0}+ years`,
            photo:
              (trainer.first_name as string).charAt(0) + (trainer.last_name as string).charAt(0),
            specializations: expertise,
            philosophy: philosophy,
            credentials: credentials,
            baseRate: first10MinRate,
            perMinuteRate: perMinuteRate.toFixed(2),
            overallRating: (trainer.average_rating as number) || 0,
            totalReviews: (trainer.total_reviews as number) || 0,
            isOnline: (trainer.isOnline as boolean) || false,
            bio: (trainer.bio as string) || "Professional dog trainer",
            reviews: [], // We'll load reviews separately if needed
          };
        });
        setActiveTrainers(transformedTrainers);
        setRefreshSuccess(true);
        setTimeout(() => setRefreshSuccess(false), 2000);
      } else {
        setActiveTrainers([]);
        setRefreshSuccess(true);
        setTimeout(() => setRefreshSuccess(false), 2000);
      }
    } catch (err) {
      console.error("Error loading trainers:", err);
      setError("Failed to load active trainers");
    } finally {
      setLoading(false);
    }
  };

  const loadActiveTrainingRequests = async (): Promise<void> => {
    try {
      setLoading(true);
      const response = (await apiService.getActiveTrainingRequests(clientId!)) as Record<
        string,
        unknown
      >;

      if (response.Code === 200 && response.data) {
        const transformedRequests = (response.data as Record<string, unknown>[]).map(
          (request: Record<string, unknown>) => ({
            ...request,
            id: (request.id || request.request_id || "") as string,
            status: (request.status || "pending") as string,
            first_10_min_rate: parseFloat(
              String(request.first_10_min_rate || request.base_rate || 0),
            ),
            per_minute_rate: parseFloat(String(request.per_minute_rate || 0)),
            total_price:
              request.total_price !== undefined && request.total_price !== null
                ? parseFloat(String(request.total_price))
                : null,
            duration_minutes: (request.duration_minutes as number | null) || null,
            created_at: (request.created_at as string | null) || null,
            accepted_at: (request.accepted_at as string | null) || null,
            payment_status: (request.payment_status as string) || "pending",
            client_joined_at: (request.client_joined_at as string | null) || null,
            trainer_joined_at: (request.trainer_joined_at as string | null) || null,
          }),
        ) as TrainingRequest[];
        setActiveTrainingRequests(transformedRequests);
      } else {
        setActiveTrainingRequests([]);
      }
    } catch (err) {
      console.error("Error loading training requests:", err);
      setError("Failed to load training requests");
    } finally {
      setLoading(false);
    }
  };

  const loadSessionHistory = async (): Promise<void> => {
    try {
      setLoading(true);
      const response = (await apiService.getClientSessions(clientId!)) as Record<string, unknown>;
      if (response.Code === 200) {
        const sessions = (response.data as Record<string, unknown>[]) || [];

        // Load ratings for all sessions
        const ratingsPromises = sessions
          .filter((session: Record<string, unknown>) => session.session_request_id)
          .map(async (session: Record<string, unknown>) => {
            try {
              const ratingResponse = (await apiService.getSessionRating(
                session.session_request_id as string,
              )) as Record<string, unknown>;
              if (ratingResponse.Code === 200 && ratingResponse.data) {
                return {
                  session_request_id: session.session_request_id as string,
                  rating: (ratingResponse.data as Record<string, unknown>).rating as number,
                };
              }
            } catch (err) {
            }
            return null;
          });

        const ratingsResults = await Promise.all(ratingsPromises);
        const ratingsMap: Record<string, number> = {};
        ratingsResults.forEach((result) => {
          if (result) {
            ratingsMap[result.session_request_id] = result.rating;
          }
        });

        // Add user_rating to each session
        const sessionsWithRatings = sessions.map((session: Record<string, unknown>) => ({
          ...session,
          user_rating: session.session_request_id
            ? ratingsMap[session.session_request_id as string]
            : null,
        }));

        setSessionHistory(sessionsWithRatings as SessionHistoryItem[]);

        // Load deliverables for all sessions that have session_request_id
        const deliverablesPromises = sessions
          .filter((session: Record<string, unknown>) => session.session_request_id)
          .map(async (session: Record<string, unknown>) => {
            try {
              const deliverablesResponse = (await apiService.getDeliverables(
                session.session_request_id as string,
              )) as Record<string, unknown>;
              if (deliverablesResponse.Code === 200 && deliverablesResponse.data) {
                return {
                  session_request_id: session.session_request_id as string,
                  data: deliverablesResponse.data,
                };
              }
            } catch (_err) {
              // No deliverables found for this session request
            }
            return null;
          });

        const deliverablesResults = await Promise.all(deliverablesPromises);
        const deliverablesMap: Record<string, DeliverableData> = {};
        deliverablesResults.forEach((result) => {
          if (result) {
            deliverablesMap[result.session_request_id] = result.data as DeliverableData;
          }
        });
        setSessionDeliverables(deliverablesMap);
      } else {
        setSessionHistory([]);
      }
    } catch (err) {
      console.error("Error loading session history:", err);
      setError("Failed to load session history");
      setSessionHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRateSession = async (session: SessionHistoryItem, rating: number): Promise<void> => {
    if (!session.session_request_id) {
      console.error("Session request ID not found");
      setError("Session request ID is required to submit a rating");
      return;
    }

    if (!session.trainer_id) {
      console.error("Trainer ID not found in session");
      setError("Trainer ID is required to submit a rating");
      return;
    }

    try {
      const response = (await apiService.submitSessionReview({
        session_request_id: session.session_request_id,
        trainer_id: session.trainer_id,
        rating: rating,
      })) as Record<string, unknown>;


      if (response.Code === 200) {
        // Update the session in state with the new rating
        setSessionHistory((prev) =>
          prev.map((s) => (s.id === session.id ? { ...s, user_rating: rating } : s)),
        );
      } else {
        setError((response.Message as string) || "Failed to submit rating");
      }
    } catch (err) {
      console.error("Error submitting rating:", err);
      setError((err as Error).message || "Failed to submit rating");
    }
  };

  const loadDeliverablesForSession = async (sessionRequestId: string): Promise<void> => {
    if (!sessionRequestId || sessionDeliverables[sessionRequestId]) {
      return; // Already loaded or no request ID
    }

    try {
      const deliverablesResponse = (await apiService.getDeliverables(sessionRequestId)) as Record<
        string,
        unknown
      >;
      if (deliverablesResponse.Code === 200 && deliverablesResponse.data) {
        setSessionDeliverables((prev) => ({
          ...prev,
          [sessionRequestId]: deliverablesResponse.data as DeliverableData,
        }));
      }
    } catch (err) {
    }
  };

  const handlePetUpdate = async (
    petId: string,
    updatedData: Record<string, unknown>,
  ): Promise<void> => {
    try {
      setLoading(true);

      // Map the local field names to API field names
      // The backend expects: age, race, pet_name, target_behaviors
      const apiData = {
        pet_name: updatedData.pet_name,
        age: parseInt(String(updatedData.age)),
        race: updatedData.race,
        target_behaviors: updatedData.target_behaviors || "",
      };

      const response = (await apiService.updatePet(petId, apiData)) as Record<string, unknown>;

      if (response.Code === 200) {
        // Reload pets after update
        await loadPets();
      } else {
        console.error("Update failed with response:", response);
        setError((response.Message as string) || "Failed to update pet");
      }
    } catch (err) {
      console.error("Error updating pet:", err);
      setError((err as Error).message || "Failed to update pet");
    } finally {
      setLoading(false);
    }
  };

  const handlePetDelete = async (petId: string): Promise<void> => {
    try {
      setLoading(true);
      await apiService.deletePet(petId);
      // Reload pets after deletion
      await loadPets();
    } catch (err) {
      console.error("Error deleting pet:", err);
      setError("Failed to delete pet");
    } finally {
      setLoading(false);
    }
  };

  const handleAddPet = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);

      // Prepare data in the format expected by the API (matching PetSetup.js)
      const petData = {
        client_id: clientId,
        pet_name: newPetData.pet_name,
        pet_age: parseInt(newPetData.age),
        pet_breed: newPetData.race,
        target_behaviors: newPetData.target_behaviors,
      };

      const response = (await apiService.createPet(petData)) as Record<string, unknown>;

      if (response.Code === 200 || response.status === "success") {
        // Reset form and close modal
        setNewPetData({
          pet_name: "",
          age: "",
          race: "",
          target_behaviors: "",
        });
        setShowAddPetModal(false);
        // Reload pets
        await loadPets();
      } else {
        setError((response.Message as string) || "Failed to add pet. Please try again.");
      }
    } catch (err) {
      console.error("Error adding pet:", err);
      setError((err as Error).message || "Failed to add pet. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleStartTraining = async (trainer: TransformedTrainer): Promise<void> => {

    setSelectedTrainer(trainer);
    setSelectedPetId(""); // Reset pet selection
    setRequestExplanation(""); // Reset explanation
    setRequestDuration(30);

    // Ensure pets are loaded before showing popup
    if (pets.length === 0) {
      await loadPets();
    }

    setShowConfirmPopup(true);
  };

  const handleConfirmTraining = async (): Promise<void> => {
    if (!selectedTrainer || !selectedPetId || !requestExplanation.trim()) {
      setError("Please select a pet and provide an explanation for the training request.");
      return;
    }

    try {
      setLoading(true);

      const response = (await apiService.createSessionRequest({
        client_id: clientId,
        trainer_id: selectedTrainer.id,
        pet_id: selectedPetId,
        explanation: requestExplanation,
        duration_minutes: requestDuration,
      })) as Record<string, unknown>;

      if (response.Code === 200) {
        // Reload active training requests to show the new request
        await loadActiveTrainingRequests();
        // Switch to requests tab to show the new request
        setActiveTab("requests");
        // Close popup
        setShowConfirmPopup(false);
        setSelectedTrainer(null);
        setSelectedPetId("");
        setRequestExplanation("");
        setRequestDuration(30);
        setError(null);
      } else {
        setError(`Error creating session request: ${response.Message as string}`);
      }
    } catch (err) {
      console.error("Error creating session request:", err);
      setError("Failed to create session request");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelTraining = (): void => {
    setShowConfirmPopup(false);
    setSelectedTrainer(null);
    setRequestDuration(30);
  };

  const handleCancelRequest = (request: TrainingRequest): void => {
    setSelectedRequest(request);
    setShowCancelPopup(true);
  };

  const handleConfirmCancel = async (): Promise<void> => {
    if (!selectedRequest) return;

    try {
      setLoading(true);
      const response = (await apiService.cancelSessionRequest(selectedRequest.id)) as Record<
        string,
        unknown
      >;

      if (response.Code === 200) {
        // Reload active training requests to remove the cancelled request
        await loadActiveTrainingRequests();
        // Close popup
        setShowCancelPopup(false);
        setSelectedRequest(null);
      }
    } catch (err) {
      console.error("Error cancelling session request:", err);
      setError("Failed to cancel session request");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelCancel = (): void => {
    setShowCancelPopup(false);
    setSelectedRequest(null);
  };

  const handlePayForRequest = async (
    request: TrainingRequest,
    coupon: string | null = null,
  ): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const checkoutData: Record<string, string> = { request_id: request.id };
      if (coupon && coupon.trim()) {
        checkoutData.coupon_code = coupon.trim();
      }
      const checkoutResponse = (await apiService.createCheckoutSession(checkoutData)) as Record<
        string,
        unknown
      >;

      if (checkoutResponse.Code === 200) {
        if (checkoutResponse.mock) {
          await apiService.verifyCheckoutSession({
            session_id: checkoutResponse.session_id,
            request_id: request.id,
            amount: checkoutResponse.amount,
          });
          await loadActiveTrainingRequests();
          alert("Payment successful! You can join the session once the trainer connects.");
          setShowCouponModal(false);
          setCouponCode("");
        } else if (checkoutResponse.url) {
          // Redirect to Stripe checkout
          window.location.href = checkoutResponse.url as string;
        } else {
          console.error("No URL in checkout response:", checkoutResponse);
          setError("Payment URL not received. Please try again.");
        }
      } else {
        setError(
          (checkoutResponse.Message as string) || "Failed to initiate payment. Please try again.",
        );
      }
    } catch (err) {
      console.error("Error initiating payment:", err);
      setError((err as Error).message || "Failed to initiate payment. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCouponModal = (request: TrainingRequest): void => {
    setSelectedRequestForPayment(request);
    setCouponCode("");
    setShowCouponModal(true);
  };

  const handleApplyCouponAndPay = (): void => {
    if (selectedRequestForPayment) {
      handlePayForRequest(selectedRequestForPayment, couponCode);
    }
  };

  const handleCloseCouponModal = (): void => {
    setShowCouponModal(false);
    setCouponCode("");
    setSelectedRequestForPayment(null);
  };

  const handleCancelAddPet = (): void => {
    setShowAddPetModal(false);
    setNewPetData({
      pet_name: "",
      age: "",
      race: "",
      target_behaviors: "",
    });
    setError(null);
  };

  const handleJoinSession = async (requestId: string): Promise<void> => {
    try {
      setLoading(true);
      const response = (await apiService.registerSessionJoin(requestId, "client")) as Record<
        string,
        unknown
      >;

      if (response.Code === 200) {
        window.location.href = `/video-call?request_id=${requestId}`;
      } else {
        setError((response.Message as string) || "Failed to join session");
      }
    } catch (err) {
      console.error("Error joining session:", err);
      setError("Failed to join session");
    } finally {
      setLoading(false);
    }
  };


  const handleTabChange = async (tabName: string): Promise<void> => {
    setActiveTab(tabName);
    setError(null);

    // Load data for the selected tab
    switch (tabName) {
      case "find-trainer":
        await loadActiveTrainers();
        break;
      case "pets":
        await loadPets();
        break;
      case "profile":
        await loadProfile();
        break;
      case "trainers":
        await loadActiveTrainers();
        break;
      case "requests":
        await loadActiveTrainingRequests();
        break;
      case "sessions":
        await loadSessionHistory();
        break;
      case "tasks":
        await loadSessionHistory(); // Load sessions to get training tasks
        break;
      default:
        break;
    }
  };

  const handleProfileEditChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setProfileEditData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (name === "password" || name === "confirmPassword") {
      setPasswordError(null);
    }
  };

  const handleProfileSave = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      setPasswordError(null);

      if (profileEditData.password) {
        if (profileEditData.password !== profileEditData.confirmPassword) {
          setPasswordError("Passwords do not match");
          return;
        }
      }

      const response = (await apiService.updateClientProfile(
        clientId!,
        profileEditData as unknown as Record<string, unknown>,
      )) as Record<string, unknown>;
      if (response.Code === 200) {
        await loadProfile();
        setIsEditingProfile(false);
        setProfileEditData({
          first_name: "",
          last_name: "",
          phone_number: "",
          address: "",
          password: "",
          confirmPassword: "",
        });
      } else {
        setError((response.Message as string) || "Failed to update profile");
      }
    } catch (err) {
      console.error("Error updating profile:", err);
      setError("Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handleProfileCancel = (): void => {
    setIsEditingProfile(false);
    setProfileEditData({
      first_name: "",
      last_name: "",
      phone_number: "",
      address: "",
      password: "",
      confirmPassword: "",
    });
    setPasswordError(null);
  };


  const handleViewSessionDetails = async (session: SessionHistoryItem): Promise<void> => {
    // Load deliverables if not already loaded
    if (session.session_request_id) {
      await loadDeliverablesForSession(session.session_request_id);
    }
    setSelectedSession(session);
    setShowSessionModal(true);
  };


  // If no clientId and not logged in, show error message
  if (!clientId && !isLoggedIn) {
    return (
      <div className="flex justify-center items-center h-screen flex-col bg-background text-foreground font-['Segoe_UI',Tahoma,Geneva,Verdana,sans-serif]">
        <h1>Access Denied</h1>
        <p>You need to be logged in to access the client portal.</p>
        <Button onClick={onBackToHome} radius="md" size="lg" className="mt-5">
          Back to Home
        </Button>
      </div>
    );
  }

  const clientTabs: PortalTab[] = [
    { key: "find-trainer", label: "Find Trainer" },
    { key: "requests", label: "Training Requests" },
    { key: "sessions", label: "Sessions" },
    { key: "profile", label: "My Profile" },
    { key: "pets", label: "My Pets" },
  ];

  return (
    <PortalLayout
      tabs={clientTabs}
      activeTab={activeTab}
      onTabChange={handleTabChange}
      onBackToHome={onBackToHome}
      onLogout={onLogout}
    >
      {activeTab === "find-trainer" && (
        <FindTrainerTab
          activeTrainers={activeTrainers}
          loading={loading}
          error={error}
          refreshSuccess={refreshSuccess}
          onRefresh={loadActiveTrainers}
          onStartTraining={handleStartTraining}
        />
      )}
      {activeTab === "pets" && (
        <PetsTab
          pets={pets}
          loading={loading}
          error={error}
          onAddPet={() => setShowAddPetModal(true)}
          onUpdatePet={handlePetUpdate}
          onDeletePet={handlePetDelete}
        />
      )}
      {activeTab === "profile" && (
        <ClientProfileTab
          profile={profile}
          isEditingProfile={isEditingProfile}
          profileEditData={profileEditData}
          passwordError={passwordError}
          loading={loading}
          error={error}
          uploadingPhoto={uploadingPhoto}
          onEditProfile={() => setIsEditingProfile(true)}
          onCancel={handleProfileCancel}
          onSave={handleProfileSave}
          onEditChange={handleProfileEditChange}
          onPhotoUpload={handleClientPhotoUpload}
        />
      )}
      {activeTab === "requests" && (
        <ClientRequestsTab
          activeTrainingRequests={activeTrainingRequests}
          loading={loading}
          error={error}
          refreshSuccess={refreshSuccess}
          onRefresh={loadActiveTrainingRequests}
          onOpenCouponModal={handleOpenCouponModal}
          onJoinSession={handleJoinSession}
          onCancelRequest={handleCancelRequest}
        />
      )}
      {activeTab === "sessions" && (
        <ClientSessionsTab
          sessionHistory={sessionHistory}
          sessionDeliverables={sessionDeliverables}
          loading={loading}
          error={error}
          selectedSession={selectedSession}
          showSessionModal={showSessionModal}
          onRefresh={loadSessionHistory}
          onRateSession={handleRateSession}
          onViewSessionDetails={handleViewSessionDetails}
          onCloseSessionModal={() => setShowSessionModal(false)}
        />
      )}

      <TrainingRequestModal
        opened={showConfirmPopup}
        trainer={selectedTrainer}
        pets={pets}
        selectedPetId={selectedPetId}
        requestDuration={requestDuration}
        requestExplanation={requestExplanation}
        loading={loading}
        onPetIdChange={setSelectedPetId}
        onDurationChange={setRequestDuration}
        onExplanationChange={setRequestExplanation}
        onCancel={handleCancelTraining}
        onConfirm={handleConfirmTraining}
      />

      <AddPetModal
        opened={showAddPetModal}
        petData={newPetData}
        error={error}
        loading={loading}
        onPetDataChange={setNewPetData}
        onCancel={handleCancelAddPet}
        onSubmit={handleAddPet}
      />

      <CancelRequestModal
        opened={showCancelPopup}
        request={selectedRequest}
        loading={loading}
        onCancel={handleCancelCancel}
        onConfirm={handleConfirmCancel}
      />

      <CouponModal
        opened={showCouponModal && selectedRequestForPayment !== null}
        request={selectedRequestForPayment}
        couponCode={couponCode}
        loading={loading}
        onCouponChange={setCouponCode}
        onCancel={handleCloseCouponModal}
        onConfirm={handleApplyCouponAndPay}
      />
    </PortalLayout>
  );
};

export default ClientPortal;
